import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import PageHead from "../components/PageHead";
import AddInstrument from "../components/AddInstrument";
import { fmt } from "../lib/signals";
import { toast } from "sonner";
import { Plus, Trash, Warning, CheckCircle, ArrowsClockwise, Briefcase } from "@phosphor-icons/react";

export default function Portfolio() {
  const [rows, setRows] = useState([]);
  const [warnings, setWarnings] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/portfolio").then((r) => { setRows(r.data.portfolio); setWarnings(r.data.warnings); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const addHolding = async (inst) => {
    // inst comes from watchlist add; also allow direct add via modal -> then create holding
    try {
      await api.post("/portfolio", { ticker: inst.ticker, exchange: inst.exchange, asset_type: inst.asset_type, yahoo_symbol: inst.yahoo_symbol });
      toast.success(`${inst.ticker} added`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const del = async (id) => { await api.delete(`/portfolio/${id}`); load(); };
  const refresh = async () => {
    setRefreshing(true);
    try { await api.post("/portfolio/refresh"); toast.success("Positions re-evaluated"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setRefreshing(false); }
  };

  return (
    <>
      <PageHead title="Portfolio Watch" subtitle="Held positions with live Defensive-Exit warnings"
        right={<div className="flex items-center gap-2">
          <button data-testid="portfolio-refresh" onClick={refresh} disabled={refreshing}
            className="glass rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-foreground/5 transition-colors duration-200">
            <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
          <button data-testid="portfolio-add" onClick={() => setShowAdd(true)}
            className="brand-gradient text-white rounded-full px-4 py-2.5 text-sm font-semibold flex items-center gap-2 shadow-lg hover:opacity-90 transition-opacity duration-200">
            <Plus size={15} weight="bold" /> Add Holding
          </button>
        </div>} />

      {warnings > 0 && (
        <div data-testid="portfolio-warning" className="flex items-center gap-2 rounded-2xl bg-bear/10 border border-bear/30 text-bear px-4 py-3 mb-4 text-sm font-medium">
          <Warning size={16} weight="fill" /> {warnings} position{warnings > 1 ? "s" : ""} flagged for defensive exit.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((h) => (
          <div key={h.id} data-testid={`holding-${h.ticker}`} className={`glass-strong rounded-3xl p-5 fade-up border ${h.warning ? "border-bear/40" : "border-transparent"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg">{h.ticker} <span className="text-muted-foreground text-sm font-normal">{h.exchange}</span></div>
                <div className="text-xs text-muted-foreground">{h.asset_type}</div>
              </div>
              <button data-testid={`holding-del-${h.ticker}`} onClick={() => del(h.id)} className="text-muted-foreground hover:text-bear transition-colors duration-200"><Trash size={16} /></button>
            </div>
            <div className="flex items-end justify-between mt-4">
              <div>
                <div className="text-xs text-muted-foreground">Last price</div>
                <div className="font-mono text-2xl font-bold">{fmt(h.last_price)}</div>
              </div>
              {h.pnl_pct != null && (
                <div className={`font-mono font-bold ${h.pnl_pct >= 0 ? "text-bull" : "text-bear"}`}>{h.pnl_pct >= 0 ? "+" : ""}{fmt(h.pnl_pct, 2)}%</div>
              )}
            </div>
            <div className={`mt-4 flex items-center gap-2 text-sm font-medium ${h.warning ? "text-bear" : "text-bull"}`}>
              {h.warning ? <Warning size={15} weight="fill" /> : <CheckCircle size={15} weight="fill" />}
              {h.warning ? "Defensive exit" : "Trend intact"}
              {h.decision && <span className="text-xs text-muted-foreground">· {h.decision}</span>}
            </div>
            <div className="text-xs text-muted-foreground mt-1">{h.reason}</div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="glass rounded-3xl p-10 text-center text-muted-foreground col-span-full">
            <Briefcase size={28} className="mx-auto mb-2 opacity-50" />
            No holdings yet. Add positions to get exit warnings when the engine turns bearish.
          </div>
        )}
      </div>

      <AddInstrument open={showAdd} onClose={() => setShowAdd(false)} onAdded={(inst) => { setShowAdd(false); if (inst) addHolding(inst); }} />
    </>
  );
}
